import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, Mail, Lock, User, Phone, MapPin, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import axios from "axios";
import { API } from "@/App";

export default function MarketplaceAuthPage() {
  const [isLogin, setIsLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", whatsapp: "", address: "", city: "" });
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const res = await axios.post(`${API}/marketplace/login`, { email: form.email, password: form.password });
        localStorage.setItem("mp_token", res.data.token);
        localStorage.setItem("mp_user", JSON.stringify(res.data));
        toast.success("Bienvenido de vuelta");
        navigate("/marketplace/mis-productos");
      } else {
        if (!form.name || !form.email || !form.password) {
          toast.error("Nombre, correo y contraseña son obligatorios");
          setLoading(false);
          return;
        }
        const res = await axios.post(`${API}/marketplace/register`, form);
        localStorage.setItem("mp_token", res.data.token);
        localStorage.setItem("mp_user", JSON.stringify(res.data));
        toast.success("Cuenta creada exitosamente");
        navigate("/marketplace/mis-productos");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al procesar");
    } finally {
      setLoading(false);
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  return (
    <Layout>
      <section className="min-h-screen bg-gray-50 flex items-center justify-center py-16 px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-lg">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-gray-900">{isLogin ? "Iniciar Sesi\u00f3n" : "Crear Cuenta"}</h1>
            <p className="text-gray-500 mt-1">{isLogin ? "Accede a tu panel de vendedor" : "Reg\u00edstrate gratis para vender"}</p>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border space-y-4" data-testid="marketplace-auth-form">
            {!isLogin && (
              <>
                <div>
                  <Label>Nombre completo *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Tu nombre" className="pl-10" data-testid="reg-name" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tel&eacute;fono</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+504..." className="pl-10" data-testid="reg-phone" />
                    </div>
                  </div>
                  <div>
                    <Label>WhatsApp</Label>
                    <div className="relative mt-1">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+504..." className="pl-10" data-testid="reg-whatsapp" />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Ciudad</Label>
                    <div className="relative mt-1">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Choluteca" className="pl-10" data-testid="reg-city" />
                    </div>
                  </div>
                  <div>
                    <Label>Direcci&oacute;n</Label>
                    <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="Barrio..." className="mt-1" data-testid="reg-address" />
                  </div>
                </div>
              </>
            )}
            <div>
              <Label>Correo electr&oacute;nico *</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="tu@correo.com" className="pl-10" data-testid="reg-email" required />
              </div>
            </div>
            <div>
              <Label>Contrase&ntilde;a *</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="M\u00ednimo 6 caracteres" className="pl-10" data-testid="reg-password" required />
              </div>
            </div>

            <Button type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-base" data-testid="auth-submit-btn">
              {loading ? "Procesando..." : isLogin ? "Iniciar Sesi\u00f3n" : "Crear mi cuenta"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>

            <div className="text-center text-sm text-gray-500">
              {isLogin ? (
                <span>&iquest;No tienes cuenta? <button type="button" onClick={() => setIsLogin(false)} className="text-emerald-600 font-medium hover:underline" data-testid="switch-register">Reg&iacute;strate gratis</button></span>
              ) : (
                <span>&iquest;Ya tienes cuenta? <button type="button" onClick={() => setIsLogin(true)} className="text-emerald-600 font-medium hover:underline" data-testid="switch-login">Inicia sesi&oacute;n</button></span>
              )}
            </div>
          </form>
        </motion.div>
      </section>
    </Layout>
  );
}
